import { memo, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, CornerDownLeft, X } from "lucide-react";
import { Button } from "../ui/button";
import { exerciseTypeIcon } from "./exerciseIcons";
import {
	EXERCISE_TYPES,
	formatExercisePrescription,
	formatExerciseSetRowLines,
	getExerciseLoadPercentage,
	matchExercisesAcrossWeeks,
} from "../../lib/programBuilderUtils";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Exercise × week matrix: the "table where I see every exercise" a coach needs
 * to judge whether a progression actually holds, instead of bouncing between
 * week tabs and holding the previous prescription in their head.
 *
 * Read-only. Rows come from `matchExercisesAcrossWeeks`, grouped by day
 * position. Cells wrap rather than truncate — a one-line summary cut off at
 * "5×5-4-3-3-2 …" tells you nothing about what was prescribed — and selecting
 * one opens the detail strip at the bottom with everything, notes included.
 */

const NAME_COLUMN_WIDTH = 176;
// Wide enough that a scheme token like "5×5-4-3-3-2" never breaks mid-token;
// the rest of the prescription wraps onto the following lines.
const WEEK_COLUMN_MIN_WIDTH = 150;

/**
 * Per-cell trend against the previous *comparable* cell of the same row.
 *
 * Only percentage loads are comparable, so conditioning, absolute kg and "BW"
 * yield no arrow at all — a made-up delta would be worse than none.
 */
const buildRowTrends = (cells) => {
	const trends = new Array(cells.length).fill(null);
	let previousValue = null;

	cells.forEach((cell, index) => {
		if (!cell) return;
		const value = getExerciseLoadPercentage(cell.exercise);
		if (value === null) return;
		if (previousValue !== null && value !== previousValue) {
			trends[index] = {
				direction: value > previousValue ? "up" : "down",
				delta: Math.round(Math.abs(value - previousValue) * 10) / 10,
				from: previousValue,
				to: value,
			};
		}
		previousValue = value;
	});

	return trends;
};

function TrendBadge({ trend }) {
	if (!trend) return null;
	const isUp = trend.direction === "up";
	const Icon = isUp ? ArrowUp : ArrowDown;
	return (
		<span
			className={`inline-flex items-center gap-0.5 text-[9px] font-mono ${
				isUp ? "text-emerald-400" : "text-amber-400"
			}`}
		>
			<Icon className="w-2.5 h-2.5" />
			{trend.delta}
		</span>
	);
}

/** Full read-out of the selected cell: the matrix itself has to stay compact. */
function CellDetail({ selection, onClose, onNavigate, t }) {
	if (!selection) return null;
	const { rowName, weekIndex, dayIndex, dayLabel, exercise, trend } = selection;
	const prescription = formatExercisePrescription(exercise, t);
	const setRowLines = formatExerciseSetRowLines(exercise);
	const notes = String(exercise?.notes || "").trim();

	return (
		<div className="shrink-0 max-h-[45%] overflow-y-auto border-t border-volt/40 bg-card">
			<div className="max-w-[420px] p-2 space-y-1">
				<div className="flex items-start gap-2">
					<div className="min-w-0 flex-1">
						<p className="text-xs text-foreground break-words">{rowName}</p>
						<p className="text-[11px] md:text-[10px] uppercase tracking-widest text-muted-foreground">
							{`${t("weekShort")} ${weekIndex + 1}`}
							{dayLabel ? ` · ${dayLabel}` : ""}
						</p>
					</div>
					{onNavigate && (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={() => onNavigate(weekIndex, dayIndex)}
							title={t("pbProgressionGoToCell")}
							aria-label={t("pbProgressionGoToCell")}
							className="h-8 w-8 md:h-6 md:w-6 shrink-0 text-muted-foreground hover:text-volt"
						>
							<CornerDownLeft className="w-3.5 h-3.5" />
						</Button>
					)}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={onClose}
						title={t("pbProgressionCloseDetail")}
						aria-label={t("pbProgressionCloseDetail")}
						className="h-8 w-8 md:h-6 md:w-6 shrink-0 text-muted-foreground hover:text-foreground"
					>
						<X className="w-3.5 h-3.5" />
					</Button>
				</div>

				{prescription && (
					<p className="font-mono text-xs md:text-[11px] text-foreground/90 whitespace-pre-wrap break-words">
						{prescription}
					</p>
				)}
				{setRowLines.length > 1 &&
					setRowLines.map((line, index) => (
						<p
							key={index}
							className="font-mono text-xs md:text-[11px] text-muted-foreground"
						>
							{line}
						</p>
					))}
				{trend && (
					<p className="text-[11px] md:text-[10px] text-muted-foreground">
						{`${t("pbDeltaVsReference")} ${t("pbDeltaTopLoad")} ${trend.from}% → ${trend.to}%`}
					</p>
				)}
				{notes && (
					<p className="text-xs md:text-[11px] text-muted-foreground/80 italic whitespace-pre-wrap break-words">
						{notes}
					</p>
				)}
			</div>
		</div>
	);
}

function ProgressionMatrixInner({ weeks, activeWeekIndex, onNavigate }) {
	const { t } = useLanguage();
	const weekList = useMemo(() => (Array.isArray(weeks) ? weeks : []), [weeks]);
	const groups = useMemo(() => matchExercisesAcrossWeeks(weekList), [weekList]);
	const [selection, setSelection] = useState(null);

	const gridTemplateColumns = `${NAME_COLUMN_WIDTH}px repeat(${weekList.length}, minmax(${WEEK_COLUMN_MIN_WIDTH}px, 1fr))`;
	// Explicit minimum instead of `min-w-max`: mixing max-content sizing with
	// `1fr` tracks is degenerate. This way the columns expand to fill a wide
	// dock and the container scrolls horizontally when they no longer fit.
	const minWidth = NAME_COLUMN_WIDTH + weekList.length * WEEK_COLUMN_MIN_WIDTH;

	if (weekList.length === 0) {
		return (
			<p className="px-3 py-6 text-xs text-muted-foreground text-center">
				{t("pbReferenceNoWeeks")}
			</p>
		);
	}

	if (groups.length === 0) {
		return (
			<p className="px-3 py-6 text-xs text-muted-foreground text-center">
				{t("pbProgressionEmpty")}
			</p>
		);
	}

	return (
		<div className="h-full flex flex-col overflow-hidden">
			<div className="flex-1 min-h-0 overflow-auto">
				<div style={{ minWidth }}>
					{/* Week header */}
					<div
						className="grid sticky top-0 z-20 bg-card border-b border-border"
						style={{ gridTemplateColumns }}
					>
						<div className="sticky left-0 z-10 bg-card border-r border-border" />
						{weekList.map((week, weekIndex) => (
							<div
								key={week?.week_id || weekIndex}
								className={`px-2 py-1.5 text-[11px] md:text-[10px] font-heading uppercase tracking-widest text-center truncate ${
									weekIndex === activeWeekIndex
										? "text-volt bg-volt/5"
										: "text-muted-foreground"
								}`}
							>
								{`${t("weekShort")} ${weekIndex + 1}`}
							</div>
						))}
					</div>

					{groups.map((group) => (
						<div key={group.dayIndex}>
							{/* Day label sticks to the left edge while the weeks scroll */}
							<div className="bg-secondary/40 border-b border-border">
								<div className="sticky left-0 inline-block px-2 py-1 text-[11px] md:text-[10px] uppercase tracking-widest text-muted-foreground">
									{group.label}
								</div>
							</div>

							{group.rows.map((row) => {
								const trends = buildRowTrends(row.cells);
								const TypeIcon = exerciseTypeIcon(row.exerciseType);
								const typeColor =
									EXERCISE_TYPES.find((item) => item.value === row.exerciseType)
										?.color || "text-volt";

								return (
									<div
										key={row.key}
										className="grid border-b border-border/40"
										style={{ gridTemplateColumns }}
									>
										<div className="sticky left-0 z-10 bg-card border-r border-border px-2 py-1.5 flex items-start gap-1.5 min-w-0">
											<TypeIcon
												className={`w-3 h-3 shrink-0 mt-0.5 ${typeColor}`}
											/>
											{/* Two lines instead of an ellipsis: exercise names here are
										    long ("Hang Snatch Below Knee") and the ellipsis made
										    different exercises indistinguishable. */}
											<span className="text-xs md:text-[11px] leading-tight text-foreground line-clamp-2 break-words">
												{row.name}
											</span>
										</div>

										{row.cells.map((cell, weekIndex) => {
											const isActiveWeek = weekIndex === activeWeekIndex;
											const isSelected =
												selection?.rowKey === row.key &&
												selection?.weekIndex === weekIndex;

											if (!cell) {
												return (
													<div
														key={weekIndex}
														className={`px-2 py-1.5 text-center text-xs md:text-[11px] text-muted-foreground/40 ${
															isActiveWeek ? "bg-volt/5" : ""
														}`}
													>
														—
													</div>
												);
											}

											const prescription =
												formatExercisePrescription(cell.exercise, t) || "·";

											return (
												<button
													key={weekIndex}
													type="button"
													onClick={() =>
														setSelection(
															isSelected
																? null
																: {
																		rowKey: row.key,
																		rowName: row.name,
																		weekIndex,
																		dayIndex: cell.dayIndex,
																		dayLabel: group.label,
																		exercise: cell.exercise,
																		trend: trends[weekIndex],
																	},
														)
													}
													aria-pressed={isSelected}
													className={`px-2 py-1.5 flex flex-col items-start gap-0.5 min-w-0 text-left hover:bg-volt/10 focus:bg-volt/10 focus:outline-none ${
														isSelected
															? "bg-volt/15 ring-1 ring-inset ring-volt/60"
															: isActiveWeek
																? "bg-volt/5"
																: ""
													}`}
												>
													{/* Wraps and clamps: a truncated single line hid the
												    prescription entirely. The detail strip has the rest. */}
													<span className="w-full font-mono text-[11px] md:text-[10px] leading-tight text-muted-foreground whitespace-normal break-words line-clamp-3">
														{prescription}
													</span>
													<TrendBadge trend={trends[weekIndex]} />
												</button>
											);
										})}
									</div>
								);
							})}
						</div>
					))}

					{/* Sticky so the legend stays readable while the weeks scroll right */}
					<div className="py-2">
						<p className="sticky left-0 inline-block px-2 max-w-[300px] text-[11px] md:text-[10px] text-muted-foreground/70">
							{t("pbProgressionLegend")}
						</p>
					</div>
				</div>
			</div>
			<CellDetail
				selection={selection}
				onClose={() => setSelection(null)}
				onNavigate={onNavigate}
				t={t}
			/>
		</div>
	);
}

// The grid can reach a few hundred cells, and the editor re-renders on every
// keystroke while `program.weeks` keeps the same identity — memoising keeps
// typing in the editor from repainting the whole matrix.
export const ProgressionMatrix = memo(ProgressionMatrixInner);
